import { useState } from "react";
import { AlertTriangle, Check, Loader2, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, SUPABASE_URL } from "@/services/api/apiUtils";

/**
 * What the buyer actually finished, step by step.
 *
 * A step is recorded "completed" only for the step the buyer is standing on
 * when they press Continue, so the record goes missing whenever they reach a
 * step another way — jumping from the step indicator, or coming back in after
 * something stopped them. Their answers are saved the whole time, but the buyer
 * site reads the record, so a finished application refuses to reach payment and
 * support has nothing to look at (Vincent Napoleon, 15-Sep).
 *
 * Every step here says what it is in one word, and offers the one action that
 * belongs at step level: write the record it is missing. Seeing the order as
 * the buyer sees it is one link for the whole order, at the top of the page —
 * a single step of a single document does not reproduce anybody's case.
 * Nothing the buyer answered is ever touched.
 */

type StepStatus =
  | "recorded"
  | "finished_not_recorded"
  | "incomplete"
  | "not_started";

interface Step {
  step_order: number;
  step_index: number;
  step_title: string;
  status: StepStatus;
  answers_saved: number;
  missing_required: string[];
  missing_required_total: number;
}

interface StepCompletion {
  order_document_id: number;
  order_id: number;
  document_id: number;
  document_name: string | null;
  total_steps: number;
  furthest_step_reached: number;
  recorded: number;
  finished_not_recorded: number;
  incomplete: number;
  not_started: number;
  blocked_by_missing_record: boolean;
  steps: Step[];
}

interface Props {
  orderDocumentId: number;
  documentName?: string;
}

const STATUS_LABEL: Record<StepStatus, string> = {
  recorded: "Done",
  finished_not_recorded: "Filled in, not recorded",
  incomplete: "Missing answers",
  not_started: "Not reached",
};

const STATUS_STYLE: Record<StepStatus, string> = {
  recorded: "border-green-200 bg-green-50 text-green-800",
  finished_not_recorded: "border-amber-300 bg-amber-100 text-amber-900",
  incomplete: "border-slate-200 bg-slate-100 text-slate-700",
  not_started: "border-slate-200 bg-white text-slate-500",
};

export const StepCompletionPanel = ({
  orderDocumentId,
  documentName,
}: Props) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingStep, setSavingStep] = useState<number | "all" | null>(null);
  // Which step the confirmation is about; "all" for the whole document.
  const [confirming, setConfirming] = useState<Step | "all" | null>(null);
  const [data, setData] = useState<StepCompletion | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const result = await apiRequest<StepCompletion>(
        `${SUPABASE_URL}/order-documents/${orderDocumentId}/step-completion`
      );
      setData(result);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Could not read the buyer's progress",
        description: error?.message || "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data) load();
  };

  const mark = async (target: Step | "all") => {
    const key = target === "all" ? "all" : target.step_index;
    try {
      setSavingStep(key);
      await apiRequest(
        `${SUPABASE_URL}/order-documents/${orderDocumentId}/step-completion`,
        {
          method: "POST",
          body: target === "all" ? {} : { step_index: target.step_index },
        }
      );
      await load();
      toast({
        title:
          target === "all"
            ? "Every step is recorded as done"
            : `Step ${target.step_index + 1} is recorded as done`,
        description:
          "The buyer can reopen the order and carry on from where they were.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Could not record the step",
        description: error?.message || "Please try again.",
      });
    } finally {
      setSavingStep(null);
      setConfirming(null);
    }
  };

  /** The whole situation in one sentence, so nobody has to add up columns. */
  const headline = (completion: StepCompletion): string => {
    if (completion.blocked_by_missing_record) {
      const stuck = completion.finished_not_recorded;
      return `The buyer filled in ${stuck} step${stuck === 1 ? "" : "s"} that the order never recorded — that is what keeps sending them back from payment.`;
    }
    if (completion.recorded === completion.total_steps) {
      return `All ${completion.total_steps} steps are recorded as done. Nothing here is blocking payment.`;
    }
    if (completion.furthest_step_reached < 0) {
      return "The buyer has not started this application yet.";
    }
    const left = completion.total_steps - completion.recorded;
    return `The buyer got as far as step ${completion.furthest_step_reached + 1} of ${completion.total_steps}; ${left} step${left === 1 ? "" : "s"} still ${left === 1 ? "needs" : "need"} filling in.`;
  };

  const remaining = data ? data.total_steps - data.recorded : 0;

  return (
    <div className="mt-3 border-t pt-3">
      <button
        type="button"
        onClick={toggle}
        className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        Buyer's progress
        <span className="text-xs">{open ? "(hide)" : "(show)"}</span>
      </button>

      {open && (
        <div className="mt-3">
          {loading && !data ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Reading the buyer's progress…
            </div>
          ) : !data ? null : data.total_steps === 0 ? (
            <p className="text-sm text-muted-foreground">
              This document has no form steps.
            </p>
          ) : (
            <>
              <div
                className={
                  "mb-3 flex items-start gap-2 rounded-md border p-3 text-sm " +
                  (data.blocked_by_missing_record
                    ? "border-amber-300 bg-amber-50 text-amber-900"
                    : "border-slate-200 bg-slate-50 text-slate-700")
                }
              >
                {data.blocked_by_missing_record && (
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                )}
                <p>{headline(data)}</p>
              </div>

              <ul className="divide-y rounded-md border">
                {data.steps.map((step) => {
                  const busy = savingStep === step.step_index;
                  return (
                    <li
                      key={step.step_order}
                      className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 text-sm"
                    >
                      <span className="w-6 flex-shrink-0 text-right text-muted-foreground">
                        {step.step_index + 1}
                      </span>

                      <span className="min-w-[8rem] flex-1 font-medium">
                        {step.step_title}
                        {step.status === "incomplete" &&
                          step.missing_required.length > 0 && (
                            <span className="block text-xs font-normal text-muted-foreground">
                              Still needs: {step.missing_required.join(", ")}
                              {step.missing_required_total >
                              step.missing_required.length
                                ? ` +${step.missing_required_total - step.missing_required.length} more`
                                : ""}
                            </span>
                          )}
                      </span>

                      <span
                        className={
                          "flex-shrink-0 rounded-full border px-2 py-0.5 text-xs " +
                          STATUS_STYLE[step.status]
                        }
                      >
                        {step.status === "recorded" && (
                          <Check className="mr-1 inline h-3 w-3" />
                        )}
                        {STATUS_LABEL[step.status]}
                      </span>

                      <span className="flex flex-shrink-0 items-center">
                        {step.status === "recorded" ? (
                          <span className="w-[6.5rem]" />
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 w-[6.5rem] text-xs"
                            disabled={savingStep !== null}
                            onClick={() => setConfirming(step)}
                          >
                            {busy ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              "Mark as done"
                            )}
                          </Button>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-2 flex items-center gap-4 text-xs">
                {remaining > 0 && (
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50"
                    disabled={savingStep !== null}
                    onClick={() => setConfirming("all")}
                  >
                    {savingStep === "all"
                      ? "Marking every step…"
                      : `Mark the remaining ${remaining} step${remaining === 1 ? "" : "s"} as done`}
                  </button>
                )}
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50"
                  disabled={loading}
                  onClick={load}
                >
                  <RotateCw className="h-3 w-3" />
                  Refresh
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <AlertDialog
        open={confirming !== null}
        onOpenChange={(next) => !next && setConfirming(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming === "all"
                ? "Record every step as done?"
                : `Record step ${(confirming?.step_index ?? 0) + 1} as done?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming === "all" ? (
                <>
                  This records all {data?.total_steps ?? ""} steps of{" "}
                  {documentName || data?.document_name || "this document"} as
                  done, so the buyer can reopen the order and continue to
                  payment.
                </>
              ) : (
                <>
                  This records "{confirming?.step_title}" as done for{" "}
                  {documentName || data?.document_name || "this document"}. The
                  other steps are left exactly as they are.
                </>
              )}{" "}
              It changes only that record — nothing the buyer answered is
              touched.
              {confirming === "all" && data && data.incomplete > 0 && (
                <>
                  {" "}
                  Note that {data.incomplete} step
                  {data.incomplete === 1 ? " is" : "s are"} still missing
                  required answers; marking them lets the buyer through without
                  filling those in.
                </>
              )}
              {confirming !== "all" && confirming?.status === "incomplete" && (
                <>
                  {" "}
                  This step is still missing {confirming.missing_required_total}{" "}
                  required answer
                  {confirming.missing_required_total === 1 ? "" : "s"}; marking
                  it lets the buyer past without filling them in.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingStep !== null}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirming && mark(confirming)}
              disabled={savingStep !== null}
            >
              {confirming === "all" ? "Mark every step" : "Mark as done"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StepCompletionPanel;
